import { Link } from 'react-router-dom';
import type { Drama } from '@/mocks/dramas';
import { STATUS_LABEL, STATUS_STYLE, categoryName, formatHeat } from '@/utils/drama';

interface DramaCardProps {
  drama: Drama;
  variant?: 'default' | 'compact';
}

export default function DramaCard({ drama, variant = 'default' }: DramaCardProps) {
  if (variant === 'compact') {
    return (
      <Link
        to={`/drama/${drama.id}`}
        className="group flex items-start gap-4 rounded-lg border border-background-200 bg-background-50 p-4 transition-colors duration-200 hover:border-primary-200 hover:bg-background-100"
      >
        <span className="mt-0.5 font-heading text-2xl font-bold text-background-400 transition-colors group-hover:text-primary-400">
          {String(drama.heat).slice(0, 2)}
        </span>
        <div className="min-w-0 flex-1">
          <h3 className="line-clamp-2 font-heading text-sm font-semibold leading-relaxed text-foreground-900 group-hover:text-primary-700">
            {drama.title}
          </h3>
          <p className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-foreground-500">
            <span>{categoryName(drama.category)}</span>
            <span className="flex items-center gap-1">
              <i className="ri-fire-line text-secondary-500"></i>
              {formatHeat(drama.heat)}
            </span>
            <span>{STATUS_LABEL[drama.status]}</span>
          </p>
        </div>
      </Link>
    );
  }

  return (
    <article className="group flex flex-col overflow-hidden rounded-lg border border-background-200 bg-background-50 transition-colors duration-200 hover:border-primary-200">
      <Link to={`/drama/${drama.id}`} className="block">
        <div className="relative h-44 w-full overflow-hidden bg-background-200">
          <img
            src={drama.cover}
            alt={`${drama.title} 时间线封面图`}
            className="h-full w-full object-top transition-transform duration-500 group-hover:scale-105"
          />
          <span
            className={`absolute left-3 top-3 rounded-full border px-2.5 py-1 text-xs font-medium ${STATUS_STYLE[drama.status]}`}
          >
            {STATUS_LABEL[drama.status]}
          </span>
        </div>
      </Link>

      <div className="flex flex-1 flex-col p-4 md:p-5">
        <div className="flex items-center gap-2 text-xs text-foreground-500">
          <span className="rounded-full bg-accent-100 px-2.5 py-1 font-medium text-accent-800">
            {categoryName(drama.category)}
          </span>
          <span className="flex items-center gap-1">
            <i className="ri-fire-line text-primary-500"></i>
            {formatHeat(drama.heat)}
          </span>
        </div>

        <h3 className="mt-3 font-heading text-base font-semibold leading-relaxed text-foreground-950">
          <Link to={`/drama/${drama.id}`} className="hover:text-primary-700">
            {drama.title}
          </Link>
        </h3>

        <p className="mt-2 line-clamp-2 flex-1 text-sm leading-relaxed text-foreground-600">
          {drama.summary}
        </p>

        <div className="mt-4">
          <div className="flex items-center justify-between text-xs text-foreground-500">
            <span className="flex items-center gap-1">
              <i className="ri-node-tree text-accent-600"></i>
              {drama.timeline.length} 个节点
            </span>
            <span>进度 {drama.progress}%</span>
          </div>
          <div className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-background-200">
            <span
              className="block h-full rounded-full bg-primary-500"
              style={{ width: `${drama.progress}%` }}
            ></span>
          </div>
        </div>

        <div className="mt-4 flex flex-wrap gap-1.5">
          {drama.tags.slice(0, 3).map((tag) => (
            <span
              key={tag}
              className="rounded-md border border-background-200 bg-background-100 px-2 py-0.5 text-xs text-foreground-600"
            >
              #{tag}
            </span>
          ))}
        </div>
      </div>
    </article>
  );
}